import SearchableInputsDropdown from '../../../components/common/SearchableDropdown';
import { useAuth } from '../../../context/AuthContext';

interface EmployeeProfileSectionProps {
    employeeName: string;
    designation?: string;
    imageUrl?: string;
    loading: boolean;
    error: any;
    employeeOptions: Array<{ key: number; name: string }>;
    selectedEmployeeName: string;
    onEmployeeSelect: (employeeId: number, employeeName: string) => void;
    employeesLoading: boolean;
}

const EmployeeProfileSection = ({ 
    employeeName, 
    designation, 
    imageUrl, 
    loading, 
    error,
    employeeOptions,
    selectedEmployeeName,
    onEmployeeSelect,
    employeesLoading
}: EmployeeProfileSectionProps) => {
    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';
    
    // Get initials from employee name
    const getInitials = (name: string) => {
        const nameParts = name.trim().split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 leftsidebar-card-dv overflow-hidden">
            {/* Header with subtle gradient background */}
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 dark:from-gray-700 dark:to-gray-800 p-4 pb-16">
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Employee Profile
                </h3>
                {(employeeID == "1000099" || employeeID == "1000103" || employeeID == "1000014") && (
                    <div className="relative z-10">
                        <SearchableInputsDropdown
                            label=""
                            placeHolder="Select an employee"
                            options={employeeOptions}
                            initialValue={selectedEmployeeName}
                            onSelect={(key: number, name: string) => onEmployeeSelect(key, name)}
                            disableDropdown={employeesLoading}
                            showSearchable={false}
                        />
                    </div>
                )}
            </div>

            {/* Profile Card - Overlapping design */}
            <div className="px-4 -mt-12 pb-6">
                {loading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                        <div className="flex items-center gap-4 animate-pulse">
                            <div className="w-20 h-20 rounded-full bg-surface-bg-secondary" />
                            <div className="flex-1 space-y-3">
                                <div className="h-5 bg-surface-bg-secondary rounded w-3/4" />
                                <div className="h-4 bg-surface-bg-secondary rounded w-1/2" />
                            </div>
                        </div>
                    </div>
                ) : error ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                        <div className="text-center py-4 text-danger-active text-sm">
                            Error loading profile
                        </div>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                        <div className="flex items-center gap-4">
                            {/* Employee Avatar with subtle ring */}
                            {imageUrl ? (
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full blur-sm opacity-40"></div>
                                    <img 
                                        src={imageUrl} 
                                        alt={employeeName}
                                        className="relative w-20 h-20 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-lg"
                                    />
                                </div>
                            ) : (
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full blur-sm opacity-40"></div>
                                    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 text-white flex items-center justify-center text-2xl font-bold border-4 border-white dark:border-gray-700 shadow-lg">
                                        {getInitials(employeeName)}
                                    </div>
                                </div>
                            )}
                            
                            {/* Employee Info */}
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">{employeeName}</h3>
                                {designation && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <span className="font-medium">{designation}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeProfileSection;
